import {
  Button,
  CircularProgress,
  CircularProgressLabel,
  useToast,
  useToken,
} from "@chakra-ui/react";
import React from "react";
import { MdSyncAlt } from "react-icons/md";

import { Organisation } from "@nens/brostar/openapi-client";
import { listToString } from "@nens/shared-utils";

import {
  importTaskIsProcessing,
  useImportTaskListQueryDirectly,
  useImportTasksLoadingStatus,
} from "@/queries/query-import-task";

import { useStartImport } from "../api/start-import-task";

type InnerProps = {
  organisation: Organisation | undefined;
};

const ImportButton: React.FC<InnerProps> = ({ organisation }) => {
  const toast = useToast();
  const startImport = useStartImport();
  const [buttonSize] = useToken("sizes", ["button"]);
  const { loadingDomains, isAnyLoading, isLoading, progress } = useImportTasksLoadingStatus();
  const importTaskListQueryDirectly = useImportTaskListQueryDirectly();

  if (organisation === undefined) return null;

  const checkAndStartImport = async () => {
    const { data, isSuccess } = await importTaskListQueryDirectly();

    if (!isSuccess || data.results.some(importTaskIsProcessing)) {
      toast({
        title: "Import already in progress",
        description: `Er is al een importtaak bezig voor ${organisation.name}. Probeer het later opnieuw.`,
        status: "warning",
        duration: 9000,
        isClosable: true,
      });
      return;
    }
    startImport(organisation);
  };

  const { name } = organisation;

  let title = "";
  if (isAnyLoading) {
    title += listToString(
      loadingDomains.map((domain) => `${domain}'s`),
      "en"
    );
    title += " van " + name + " aan het laden";
  }

  return (
    <Button
      boxSize="button"
      p={0}
      variant="outlinePrimary"
      onClick={checkAndStartImport}
      title={title}
      isLoading={isAnyLoading || isLoading}
      spinner={
        <CircularProgress
          isIndeterminate={progress === undefined}
          size={`calc(${buttonSize} - 8px)`}
          value={progress}
          color="primary"
          fontSize={buttonSize}
        >
          <CircularProgressLabel color="primary">{progress ?? 0}%</CircularProgressLabel>
        </CircularProgress>
      }
    >
      <MdSyncAlt />
    </Button>
  );
};

export { ImportButton };
